import { Dispatch, SetStateAction } from "react";
import * as XLSX from "xlsx";
import {
  Modal,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Paper,
  Chip,
  Alert,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
} from "@mui/icons-material";

// Style for the modal
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: "90%", md: "85%" },
  maxWidth: 1000,
  maxHeight: "90vh",
  overflow: "auto",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 2,
  p: { xs: 2, sm: 3, md: 4 },
};

// Sample data for the table
const sampleData = [
  {
    id: 1,
    enterprise: "Enterprise A",
    nhg: "NHG A",
    ads: "ADS A",
    amount: "50000",
    months: "24",
    sanctioned: "2021-11-03",
    transfer: "2022-01-03",
    repaymentStart: "2022-02-01",
    demandRepayment: "Demand: 8333\nRepayment: 8333",
  },
];

export default function ExcelStructureModal({ open, setOpen }: {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}) {
  // const [open, setOpen] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleDownloadTemplate = () => {
    const header = [
      ["Loan ID", "Member Name", "Principal Amount", "Interest Rate", "Start Date", "End Date"],
    ];
    const sampleData = [
      ["1", "John Doe", 10000, "4%", "2025-01-01", "2025-12-31"],
      ["2", "Jane Smith", 5000, "4%", "2025-02-01", "2025-12-31"],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet([...header, ...sampleData]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Loans Template");

    XLSX.writeFile(workbook, "loan_template.xlsx");
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Paper sx={modalStyle}>
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2}
          >
            <Typography variant="h5" fontWeight="600" color="primary">
              Excel Upload Format Guide
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Information Alert */}
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            Prepare your Excel file with the following structure. Each row represents one loan. Columns after column 12 should contain Demand/Repayment pairs for each month.
          </Alert>

          {/* Download Template Button */}
          {/* <Box mb={3}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              Download Template
            </Button>
          </Box> */}

          {/* Table Container with Scroll for small screens */}
          <Box
            sx={{
              overflowX: "auto",
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              mb: 2,
            }}
          >
            <Table
              sx={{
                minWidth: 800, // Ensures table has minimum width on small screens
                "& .MuiTableCell-root": {
                  py: 1.5,
                  fontSize: "0.875rem",
                },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: theme.palette.grey[50],
                    "& .MuiTableCell-head": {
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    },
                  }}
                >
                  <TableCell>Loan ID</TableCell>
                  <TableCell>Enterprise Name</TableCell>
                  <TableCell>NHG Name</TableCell>
                  <TableCell>ADS Name</TableCell>
                  <TableCell>Applied Amount</TableCell>
                  <TableCell>Repayment Months</TableCell>
                  <TableCell>Sanctioned Date</TableCell>
                  <TableCell>Fund Transfer Date</TableCell>
                  <TableCell>Repayment Start Date</TableCell>
                  <TableCell>Demand / Repayment</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sampleData.map((row) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      "&:nth-of-type(even)": {
                        backgroundColor: theme.palette.action.hover,
                      },
                      "&:hover": {
                        backgroundColor: theme.palette.action.selected,
                      },
                    }}
                  >
                    <TableCell>
                      <Chip label={row.id} size="small" color="primary" />
                    </TableCell>
                    <TableCell>{row.enterprise}</TableCell>
                    <TableCell>{row.nhg}</TableCell>
                    <TableCell>{row.ads}</TableCell>
                    <TableCell>₹{row.amount}</TableCell>
                    <TableCell>{row.months}</TableCell>
                    <TableCell>{row.sanctioned}</TableCell>
                    <TableCell>{row.transfer}</TableCell>
                    <TableCell>{row.repaymentStart}</TableCell>
                    <TableCell>
                      <Box>
                        <Chip
                          label="Demand: 8333"
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ mb: 0.5, fontSize: "0.7rem" }}
                        />
                        <Chip
                          label="Repayment: 8333"
                          size="small"
                          variant="outlined"
                          color="secondary"
                          sx={{ fontSize: "0.7rem" }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {/* Important Notes */}
          <Box
            sx={{
              backgroundColor: theme.palette.warning.light,
              p: 2,
              borderRadius: 2,
              mb: 3,
            }}
          >
            <Typography variant="subtitle2" fontWeight="600" gutterBottom>
              Important Notes:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              <li>
                <Typography variant="body2">
                  Columns after column 11 should follow the pattern:{" "}
                  <b>Demand, Repayment, Demand, Repayment, ...</b> for each month.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Ensure dates are formatted as YYYY-MM-DD.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Amount values should be numbers without currency symbols.
                </Typography>
              </li>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleDownloadTemplate}
              startIcon={<DownloadIcon />}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              Download Template
            </Button>
          </Box>
        </Paper>
      </Modal>
    </>
  );
}